/**
 * Human identity on screen (BACKLOG P1, P2): how a user is named, where their creator profile lives and which
 * face they get. An uploaded `avatarUrl` wins; otherwise the mascot is derived from the handle (or the wallet
 * address before a handle exists), so every person looks different and stable across devices.
 */
import type { AgentAvatar } from '@algoboo/schema/api'
import { defaultAvatarFor, shortAddress } from '@algoboo/ui'

export interface UserLike {
  handle: string | null
  displayName?: string | null
  primaryAddress?: string | null
  avatarUrl?: string | null
}

export type UserFace = { kind: 'image'; url: string } | { kind: 'mascot'; avatar: AgentAvatar }

/** Seed for the derived mascot: `u:` keeps people apart from agents that happen to share a string. */
export function userAvatarSeed(u: UserLike): string {
  return `u:${(u.handle ?? u.primaryAddress ?? 'anon').toLowerCase()}`
}

export function userFace(u: UserLike): UserFace {
  if (u.avatarUrl && isSafeAvatarUrl(u.avatarUrl)) return { kind: 'image', url: u.avatarUrl }
  return { kind: 'mascot', avatar: defaultAvatarFor(userAvatarSeed(u)) }
}

/** What the API accepts for `PatchMe.avatarUrl` (https, or the media host); http only for localhost dev. */
export function isSafeAvatarUrl(url: string): boolean {
  try {
    const u = new URL(url)
    if (u.protocol === 'https:') return true
    return u.protocol === 'http:' && (u.hostname === '127.0.0.1' || u.hostname === 'localhost')
  } catch {
    return false
  }
}

/** `@handle`, else the display name, else the short address. */
export function userLabel(u: UserLike): string {
  if (u.handle) return `@${u.handle}`
  if (u.displayName) return u.displayName
  return u.primaryAddress ? shortAddress(u.primaryAddress, 6, 4) : 'someone'
}

/** Creator profile path, or null before the user picked a handle. */
export function creatorPath(handle: string | null | undefined): string | null {
  return handle ? `/u/${handle}` : null
}
